/**
 * Request cache
 *
 * A short-lived, in-memory cache for GET responses, wired in as an axios
 * adapter so a service function opts in with `{ cache: ... }` and nothing
 * else in the app changes: screens keep their own loading states, they just
 * resolve instantly when the answer is still fresh.
 *
 * Three rules keep it honest:
 *
 *  - Every write through the same client forgets everything. A screen that
 *    saves and then re-reads always sees its own change; the only staleness
 *    left is other people's, bounded by the TTL.
 *  - Identical in-flight GETs share one request, so three widgets asking for
 *    the same dropdown options cost one round trip.
 *  - Consumers get a copy, never the cached object. Screens mutate responses
 *    (sorting in place is common) and must not corrupt what the next one sees.
 *
 * The key includes Accept-Language: the server resolves course names and
 * announcements into the caller's language, so the same URL is a different
 * answer per language.
 */

import axios from 'axios';

const DEFAULT_TTL_MS = 60_000;
const MAX_ENTRIES = 300;

/** TTL presets, chosen by how fast the answer changes underneath the user. */
export const CACHE_TTL = {
  /** Dropdown options and other reference lists: rooms, courses, periods. */
  REFERENCE: 5 * 60_000,
  /** A user's own profile and other people's public profiles. */
  PROFILE: 60_000,
  /** Dashboards and list screens, so back-navigation is instant. */
  SCREEN: 30_000,
};

const entries = new Map(); // key -> { expiresAt, response }
const inflight = new Map(); // key -> Promise<response>

// Bumped on every write. A GET that started before the write must not be
// stored once it lands: it may describe the world the write just changed.
let generation = 0;

const clone = (value) => {
  if (value === null || typeof value !== 'object') return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      // Falls through for anything structuredClone refuses (functions etc.).
    }
  }
  return JSON.parse(JSON.stringify(value));
};

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] ?? headers[name.toLowerCase()];
};

const cacheKey = (config) =>
  `${(config.method || 'get').toLowerCase()} ${axios.getUri(config)} lang=${
    readHeader(config.headers, 'Accept-Language') || ''
  }`;

const ttlOf = (cacheOption) => {
  if (cacheOption === true) return DEFAULT_TTL_MS;
  if (cacheOption && typeof cacheOption === 'object') return Number(cacheOption.ttl) || DEFAULT_TTL_MS;
  return 0;
};

const isCacheable = (config) =>
  ttlOf(config.cache) > 0 &&
  (config.method || 'get').toLowerCase() === 'get' &&
  // Blobs are not cloneable the way JSON is and have their own cache (the
  // browser's, and mediaCache for chat media).
  (!config.responseType || config.responseType === 'json');

const store = (key, response, ttl) => {
  if (entries.size >= MAX_ENTRIES) {
    // Map iterates in insertion order, so the first key is the oldest.
    entries.delete(entries.keys().next().value);
  }
  entries.set(key, { expiresAt: Date.now() + ttl, response });
};

const fromCache = (cached, config) => ({
  ...cached.response,
  data: clone(cached.response.data),
  config,
  cached: true,
});

/** Forget every cached answer. In-flight requests are left to finish. */
export const invalidateRequestCache = () => {
  generation += 1;
  entries.clear();
};

export const clearRequestCache = invalidateRequestCache;

/**
 * Wrap an axios instance's adapter. Call once per instance, right after
 * `axios.create`.
 */
export const withRequestCache = (instance) => {
  // A stubbed axios (the screen tests fake it) has no adapters to wrap.
  if (typeof axios.getAdapter !== 'function' || !instance?.defaults) return instance;

  const baseAdapter = axios.getAdapter(instance.defaults.adapter || axios.defaults.adapter);

  instance.defaults.adapter = async (config) => {
    const method = (config.method || 'get').toLowerCase();

    if (method !== 'get' && method !== 'head' && method !== 'options') {
      try {
        return await baseAdapter(config);
      } finally {
        // Even a failed write may have changed something on the way to failing.
        invalidateRequestCache();
      }
    }

    if (!isCacheable(config)) {
      return baseAdapter(config);
    }

    const key = cacheKey(config);
    const cached = entries.get(key);
    if (cached) {
      if (cached.expiresAt > Date.now()) {
        return fromCache(cached, config);
      }
      entries.delete(key);
    }

    const pending = inflight.get(key);
    if (pending) {
      return pending.then((response) => ({
        ...response,
        data: clone(response.data),
        config,
        cached: true,
      }));
    }

    const startedIn = generation;
    const request = baseAdapter(config)
      .then((response) => {
        if (startedIn === generation) {
          // Just the answer — not the XHR or the config that produced it.
          const { status, statusText, headers, data } = response;
          store(key, { status, statusText, headers, data: clone(data) }, ttlOf(config.cache));
        }
        return response;
      })
      .finally(() => {
        inflight.delete(key);
      });

    inflight.set(key, request);
    return request;
  };

  return instance;
};
